import type { WorldRoomEntry } from "@cleo/contracts";
import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useEffect, useEffectEvent } from "react";
import { onDeskCommand } from "../deskCommandBus";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "./ui/menu";
import { useNewGeneralRoom } from "../neucleos/useNewGeneralRoom";
export { NEW_ROOM_LABEL } from "../neucleos/useNewGeneralRoom";

/**
 * The dock + births a general room on the defaults alone (2026-09-10): no
 * modal, no question. The room is named NEW_ROOM_LABEL until the person or
 * the naming agent renames it; its first session takes the root-session
 * defaults (useRootSessionDefaults) — the composer's own picker switches the
 * model before the first Send. A room's own controls still own session birth.
 */
/** An entitled Workroom the + also offers where no room card carries its own +. */
export interface NewRoomMenuWorkroom {
  readonly workroomKey: string;
  readonly title: string;
}

export function NewGeneralRoomButton({
  worldKey,
  acceptEntryReadback,
  appearance = "deck",
  workrooms = [],
  onNewWorkroomSession,
}: {
  readonly worldKey: string;
  /**
   * `card`: the room card's own + (the principal, 16 Oct 2026: General's +
   * matches every other card's); `deck`: the ringed deck plus.
   */
  readonly appearance?: "deck" | "card";
  /** The room and its first session as the World read them back after the birth. */
  readonly acceptEntryReadback: (entry: WorldRoomEntry) => boolean | void;
  /**
   * The tight dock and the Sessions view have no room cards (W-d416f930): the
   * + then offers New room and each entitled Workroom, so a Workroom with no
   * session can still be started. Empty keeps the plain + (the Rooms view).
   */
  readonly workrooms?: ReadonlyArray<NewRoomMenuWorkroom>;
  readonly onNewWorkroomSession?: (workroom: NewRoomMenuWorkroom) => void;
}) {
  const navigate = useNavigate();
  const { create: openRoom, submitting, error } = useNewGeneralRoom(worldKey);
  async function create() {
    const outcome = await openRoom();
    if (!outcome) return;
    acceptEntryReadback(outcome.entry);
    await navigate({
      to: "/session/$sessionId",
      params: { sessionId: outcome.sessionId },
      search: { world: worldKey },
    } as never);
  }
  // HIG8: the Mac File menu's New Room lands on the same birth as the dock's +.
  const createFromMenu = useEffectEvent(() => {
    if (!submitting) void create();
  });
  useEffect(() => onDeskCommand("new-room", createFromMenu), []);
  const buttonProps = {
    "aria-label": "New general room",
    "data-world-room-new": "",
    // W-799b926f: the World this button creates its room in.
    "data-world-key": worldKey,
    "data-deck-plus": appearance === "deck" ? "" : undefined,
    "data-room-card-new": appearance === "card" ? "general" : undefined,
    className:
      appearance === "card"
        ? "ncl-room-icon-button ncl-room-new disabled:opacity-50"
        : "flex size-[var(--deck-plus-size)] shrink-0 cursor-pointer items-center justify-center self-center rounded-full text-foreground/80 hover:text-foreground disabled:opacity-50",
    disabled: submitting,
    title: error ?? "New room",
    type: "button" as const,
  };
  const plus =
    appearance === "card" ? (
      <Plus aria-hidden size={16} />
    ) : (
      <Plus aria-hidden className="size-3" strokeWidth={2.2} />
    );
  const offered = onNewWorkroomSession === undefined ? [] : workrooms;
  return (
    <span className="relative inline-flex items-center">
      {offered.length === 0 ? (
        <button {...buttonProps} onClick={() => void create()}>
          {plus}
        </button>
      ) : (
        <Menu>
          <MenuTrigger {...buttonProps}>{plus}</MenuTrigger>
          <MenuPopup align="start" side="right">
            <MenuItem data-new-room-choice="general" onClick={() => void create()}>
              New room
            </MenuItem>
            {offered.map((workroom) => (
              <MenuItem
                key={workroom.workroomKey}
                data-new-room-choice={`workroom:${workroom.workroomKey}`}
                onClick={() => onNewWorkroomSession?.(workroom)}
              >
                {`New session in ${workroom.title}`}
              </MenuItem>
            ))}
          </MenuPopup>
        </Menu>
      )}
      {error ? (
        <span
          className="absolute left-full top-1/2 z-10 ml-2 w-56 -translate-y-1/2 rounded-md border border-border/60 bg-background px-2 py-1 text-[11px] text-warning"
          role="alert"
        >
          {error} Click + again to retry.
        </span>
      ) : null}
    </span>
  );
}
